import React, { useState, useRef, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Heart, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import type { ReactionType } from '../types/database';

interface ReactionConfig {
  type: ReactionType;
  icon: React.ReactNode;
  label: string;
  color: string;
  hoverColor: string;
}

const REACTIONS: ReactionConfig[] = [
  {
    type: 'like',
    icon: <ThumbsUp className="h-5 w-5" />,
    label: 'Like',
    color: 'text-blue-500',
    hoverColor: 'hover:bg-blue-50'
  },
  {
    type: 'love',
    icon: <Heart className="h-5 w-5" />,
    label: 'Love',
    color: 'text-red-500',
    hoverColor: 'hover:bg-red-50'
  },
  {
    type: 'wow',
    icon: <Sparkles className="h-5 w-5" />,
    label: 'Wow',
    color: 'text-yellow-500',
    hoverColor: 'hover:bg-yellow-50'
  },
  {
    type: 'dislike',
    icon: <ThumbsDown className="h-5 w-5" />,
    label: 'Dislike',
    color: 'text-gray-500',
    hoverColor: 'hover:bg-gray-50'
  }
];

interface ReactionPickerProps {
  currentReaction?: ReactionType | null;
  onReactionSelect: (reaction: ReactionType) => void;
  compact?: boolean;
}

export default function ReactionPicker({
  currentReaction,
  onReactionSelect,
  compact = false
}: ReactionPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    }

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPicker]);

  const handleReactionClick = (reactionType: ReactionType) => {
    onReactionSelect(reactionType);
    setShowPicker(false);
  };

  const getCurrentReactionConfig = () => {
    return REACTIONS.find(r => r.type === currentReaction);
  };

  const currentConfig = getCurrentReactionConfig();

  return (
    <div className="relative" ref={pickerRef}>
      <button
        onClick={() => setShowPicker(!showPicker)}
        className={cn(
          "flex items-center space-x-2 px-4 py-2 rounded-lg transition-all",
          currentReaction
            ? `${currentConfig?.color} ${currentConfig?.hoverColor}`
            : "text-gray-500 hover:bg-gray-50"
        )}
      >
        {currentConfig ? (
          <>
            <span className={cn(currentConfig.color)}>
              {currentConfig.icon}
            </span>
            {!compact && <span>{currentConfig.label}</span>}
          </>
        ) : (
          <>
            <ThumbsUp className="h-5 w-5" />
            {!compact && <span>React</span>}
          </>
        )}
      </button>

      {showPicker && (
        <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 p-2 flex space-x-1 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {REACTIONS.map((reaction) => (
            <button
              key={reaction.type}
              onClick={() => handleReactionClick(reaction.type)}
              className={cn(
                "p-2 rounded-lg transition-all transform hover:scale-110",
                reaction.hoverColor,
                currentReaction === reaction.type && "bg-gray-100"
              )}
              title={reaction.label}
            >
              <span className={reaction.color}>
                {reaction.icon}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function getReactionConfig(type: ReactionType): ReactionConfig {
  return REACTIONS.find(r => r.type === type) || REACTIONS[0];
}

export function ReactionIcon({ type, className }: { type: ReactionType; className?: string }) {
  const config = getReactionConfig(type);
  return (
    <span className={cn(config.color, className)}>
      {config.icon}
    </span>
  );
}
